import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  CircularProgress,
  Divider,
  Input, InputAdornment, ListItemIcon, MenuItem, Select, TextField,
} from '@mui/material';
import { valuePropType } from './itemField';
import { CheckedIcon, NoIcon } from '../../components/Styled/Buttons/icons';
import api from '../../api/req';
import { AppContext } from '../../providers/authProvider';

function valueRenderer(values) {
  return values.map((v) => v.repr).join(', ');
}
function MultiItemSelect({
  value,
  onChange,
  backend,
  filter,
  disabled,
  margin,
  readOnly,
  className,
}) {
  const { auth } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [values, setValues] = useState([]);
  const onSearch = useCallback(
    (s) => {
      const loader = async () => {
        const r = await api.get(`${backend}`, auth, { search: s, ...filter });
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return r.json();
      };
      setLoading(true);
      setErr(false);
      loader()
        .then((d) => {
          const result = Array.isArray(d) ? d : d.results;
          setValues(result.filter((dd) => !value.map((vv) => vv.id)
            .includes(dd.id)));
        })
        .catch((e) => setErr(e.message))
        .finally(setLoading(false));
    },
    [auth, backend, filter, value],
  );

  const onSearchChange = useCallback(
    (e) => {
      setSearch(e.target.value);
    },
    [],
  );

  useEffect(
    () => onSearch(search),
    [onSearch, search],
  );
  const sSearchAddProps = useMemo(
    () => (loading ? ({
      InputProps: {
        endAdornment: (
          <InputAdornment position="end">
            <CircularProgress size={16} color="inherit" />
          </InputAdornment>
        ),
      },
    }) : {}),
    [loading],
  );
  return (
    <Select
      multiple
      value={value}
      onChange={(e) => onChange(e, e.target.value)}
      readOnly={readOnly}
      disabled={disabled}
      className={className}
      variant="standard"
      fullWidth
      margin={margin}
      renderValue={valueRenderer}
      input={(
        <Input
          endAdornment={(
            <InputAdornment position="end" sx={{ mr: 3 }}>
              {loading && (
              <CircularProgress size={20} />
              )}
            </InputAdornment>
          )}
        />
      )}
    >
      {[
        ...value.map((v) => (
          <MenuItem key={v.id} value={v}>
            <ListItemIcon>
              <img src={CheckedIcon} alt="" />
            </ListItemIcon>
            {v.repr}
          </MenuItem>
        )),
        <Divider />,
        <MenuItem>
          <TextField
            value={search}
            onChange={onSearchChange}
            onKeyDown={(e) => e.stopPropagation()}
            placeholder="Пошук"
            error={!!err}
            helperText={err}
            fullWidth
            margin={margin}
            {...sSearchAddProps}
          />
        </MenuItem>,
        ...values.map((v) => (
          <MenuItem key={v.id} value={v}>
            <ListItemIcon>
              <img src={NoIcon} alt="icon" />
            </ListItemIcon>
            {v.repr}
          </MenuItem>
        )),
      ]}
    </Select>
  );
}

MultiItemSelect.propTypes = {
  value: PropTypes.arrayOf(valuePropType),
  onChange: PropTypes.func.isRequired,
  backend: PropTypes.string.isRequired,
  filter: PropTypes.shape({}),
  disabled: PropTypes.bool,
  margin: PropTypes.oneOf(['none', 'normal', 'dense']),
  readOnly: PropTypes.bool,
  className: PropTypes.string,
};

MultiItemSelect.defaultProps = {
  value: [],
  filter: null,
  disabled: false,
  margin: 'dense',
  readOnly: false,
  className: '',
};

export default MultiItemSelect;
